import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import { z } from "zod"

import { type AuthStrategy } from "../../EditorConfig"
import { repositoryFramework } from "./repository"

export interface OnboardingStep {
  id: OnboardingStepId
  title: string
  description: string
  content?: JSX.Element
  videoUrl?: string
  defaultCompleted?: boolean
}

export const OnboardingStepId = z.enum([
  "createPrismicProject",
  "chooseLocale",
  "createProject",
  "setupSliceMachine",
  "createPageType",
  "codePage",
  "createSlice",
  "reviewAndPush",
  "createContent",
  "renderPage",
])

export type OnboardingStepId = z.infer<typeof OnboardingStepId>

export const onboardingStateSchema = z.object({
  completedSteps: z.array(z.string()),
  isDismissed: z.boolean(),
  context: z.object({
    framework: repositoryFramework,
    starterId: z.string().nullable().optional(),
  }),
})

export type OnboardingState = z.infer<typeof onboardingStateSchema>
export type OnboardingFramework = OnboardingState["context"]["framework"]

export const onboardingToggleStepResultSchema = z.object({
  completedSteps: z.array(z.string()),
})

export type OnboardingToggleStepResult = z.infer<
  typeof onboardingToggleStepResultSchema
>

export const onboardingToggleResultSchema = z.object({
  isDismissed: z.boolean(),
})

export type OnboardingToggleResult = z.infer<
  typeof onboardingToggleResultSchema
>

export function getOnboarding(
  baseUrl: URL,
  repository: string,
  authStrategy: AuthStrategy,
): Promise<OnboardingState> {
  const url = new URL("onboarding", baseUrl)
  url.searchParams.append("repository", repository)
  return request(url, {
    headers: { repository },
    credentials: getRequestCredentials(authStrategy),
    okType: onboardingStateSchema,
  })
}

export function toggleOnboardingStep(
  stepId: string,
  baseUrl: URL,
  repository: string,
  authStrategy: AuthStrategy,
): Promise<OnboardingToggleStepResult> {
  const url = new URL(`onboarding/${stepId}/toggle`, baseUrl)
  url.searchParams.append("repository", repository)
  return request(url, {
    method: "PATCH",
    headers: { repository },
    credentials: getRequestCredentials(authStrategy),
    okType: onboardingToggleStepResultSchema,
  })
}

export function toggleOnboarding(
  baseUrl: URL,
  repository: string,
  authStrategy: AuthStrategy,
): Promise<OnboardingToggleResult> {
  const url = new URL(`onboarding/toggle`, baseUrl)
  url.searchParams.append("repository", repository)
  return request(url, {
    method: "PATCH",
    headers: { repository },
    credentials: getRequestCredentials(authStrategy),
    okType: onboardingToggleResultSchema,
  })
}

export type OnboardingTrackSource = "PageBuilder" | "SliceMachine"

export type OnboardingTrackEvent =
  | {
      event: "shared-onboarding:step-completed"
      stepId: string
      stepTitle: string
      source: OnboardingTrackSource
    }
  | {
      event: "shared-onboarding:step-opened"
      stepId: string
      stepTitle: string
      source: OnboardingTrackSource
    }
  | {
      event: "shared-onboarding:completed"
      source: OnboardingTrackSource
    }
  | {
      event: "shared-onboarding:follow-tutorial"
      source: OnboardingTrackSource
    }

export interface OnboardingTracking {
  track: (args: OnboardingTrackEvent) => void | Promise<void>
  source: OnboardingTrackSource
}
